import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2, Mail, Paperclip, Send, UserCheck, X } from 'lucide-react';
import { ADRESSAT_LABEL } from './fragenkatalog';
import type { FrageAdressat } from './types';
import {
    anforderungBetreff,
    anforderungMailText,
    datumDeutsch,
    pruefeAnforderungsEmpfaenger,
    type AnforderungsEmpfaenger,
    type UnterlagenZeile,
} from './unterlagenLogic';

/** Was der Dialog zurueckgibt — Empfaenger plus Betreff und Text, wie sie
 *  der Mensch zuletzt gesehen hat. */
export interface VersandEingabe extends AnforderungsEmpfaenger {
    betreff: string;
    text: string;
}

/** Der vorab gelesene Empfaenger aus der Zuordnung Adressat → Person. */
export interface EmpfaengerVorbelegung extends AnforderungsEmpfaenger {
    /** `gefunden` = in der Zuordnung eingetragen; `leer` = niemand zugeordnet. */
    stand: 'gefunden' | 'leer';
    /** Spalte fehlt, Lesen gescheitert … — wird gezeigt, sperrt aber nicht. */
    hinweis: string | null;
}

interface Props {
    lieferant: FrageAdressat;
    /** Die Unterlagen, die auf dem Blatt stehen (unerledigt, dieser Lieferant). */
    zeilen: UnterlagenZeile[];
    projektName?: string;
    projektNummer?: string;
    /** Rueckmeldefrist (ISO-Tag), die nach dem Versand gestempelt wird — steht im Text. */
    fristIso: string;
    absender?: string;
    vorbelegung: EmpfaengerVorbelegung;
    /** Versand unterwegs — Eingaben gesperrt, kein zweiter Klick. */
    laeuft: boolean;
    /** Fehler des letzten Versuchs (Mail nicht raus). Der Dialog bleibt offen. */
    fehler: string | null;
    onVersenden: (eingabe: VersandEingabe) => void;
    onAbbrechen: () => void;
}

/**
 * „Versenden …" einer Unterlagenanforderung an EINEN Lieferanten (Plan 4.1).
 *
 * Der Dialog sammelt nur: Empfaenger (vorbelegt aus der Zuordnung Adressat →
 * Person der Planungsphase, die der Aufrufer vorab gelesen hat — sonst
 * Eingabe), Betreff und Text (vorbelegt, aenderbar). Erzeugen des Blatts,
 * Versand und Stempel liegen beim Aufrufer (`UnterlagenCheckliste`) — der
 * Dialog weiss nicht, wie verschickt wird. Sein Zustand entsteht einmal aus
 * den Props beim Oeffnen; kein Effekt, kein Nachladen.
 *
 * Per Portal an `document.body` (Muster `HandbuchImportDialog`): der
 * Modulinhalt scrollt in einem eigenen Container mit `transform`.
 */
export const AnforderungVersandDialog: React.FC<Props> = ({
    lieferant, zeilen, projektName, projektNummer, fristIso, absender, vorbelegung, laeuft, fehler, onVersenden, onAbbrechen,
}) => {
    const [name, setName] = useState(vorbelegung.name);
    const [email, setEmail] = useState(vorbelegung.email);
    const [betreff, setBetreff] = useState(() => anforderungBetreff(projektName, projektNummer));
    const [text, setText] = useState(() => anforderungMailText({
        empfaengerName: vorbelegung.name, projektName, projektNummer, unterlagen: zeilen.map(z => z.name), fristIso, absender,
    }));
    const [meldung, setMeldung] = useState<string | null>(null);

    const absenden = () => {
        const problem = pruefeAnforderungsEmpfaenger({ name, email });
        if (problem) { setMeldung(problem); return; }
        if (!betreff.trim()) { setMeldung('Bitte einen Betreff eintragen.'); return; }
        setMeldung(null);
        onVersenden({ name: name.trim(), email: email.trim(), betreff: betreff.trim(), text });
    };

    const gesperrt = laeuft;

    return createPortal(
        <div className="fixed inset-0 z-[80] bg-slate-900/50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
                <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-200">
                    <Mail className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <h3 className="font-bold text-slate-800">Unterlagenanforderung versenden</h3>
                        <p className="text-xs text-slate-500 leading-snug">
                            An: {ADRESSAT_LABEL[lieferant] || lieferant} ({lieferant}) · {zeilen.length} Unterlage{zeilen.length === 1 ? '' : 'n'}.
                            Das Anforderungsblatt wird als PDF angehängt und in der Projektablage festgehalten; erst nach
                            erfolgreichem Versand gelten die Unterlagen als angefordert (Rückmeldung bis {datumDeutsch(fristIso)}).
                        </p>
                    </div>
                    <button type="button" onClick={onAbbrechen} disabled={gesperrt} className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-40" aria-label="Schließen">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="px-5 py-4 space-y-4 overflow-y-auto">
                    {/* ─── Empfaenger ────────────────────────────────────── */}
                    <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Empfänger</span>
                            {vorbelegung.stand === 'gefunden' ? (
                                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600"><UserCheck className="w-3 h-3" /> aus der Empfänger-Zuordnung</span>
                            ) : (
                                <span className="text-[11px] text-amber-700">Für {lieferant} ist niemand zugeordnet — bitte eintragen (Zuordnung im Reiter „Fragen").</span>
                            )}
                        </div>
                        {vorbelegung.hinweis && (
                            <div className="text-[11px] text-amber-700 mb-1">{vorbelegung.hinweis}</div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <input
                                type="text"
                                value={name}
                                disabled={gesperrt}
                                autoFocus={!vorbelegung.name}
                                onChange={ev => { setName(ev.target.value); setMeldung(null); }}
                                placeholder="Name (Pflicht)"
                                aria-label="Name des Empfängers"
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                            />
                            <input
                                type="email"
                                value={email}
                                disabled={gesperrt}
                                autoFocus={!!vorbelegung.name && !vorbelegung.email}
                                onChange={ev => { setEmail(ev.target.value); setMeldung(null); }}
                                placeholder="E-Mail (Pflicht)"
                                aria-label="E-Mail des Empfängers"
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                            />
                        </div>
                    </div>

                    {/* ─── Betreff und Text ──────────────────────────────── */}
                    <label className="block">
                        <span className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">Betreff</span>
                        <input
                            type="text"
                            value={betreff}
                            disabled={gesperrt}
                            onChange={ev => { setBetreff(ev.target.value); setMeldung(null); }}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                        />
                    </label>
                    <label className="block">
                        <span className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">Text</span>
                        <textarea
                            value={text}
                            disabled={gesperrt}
                            onChange={ev => setText(ev.target.value)}
                            rows={10}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm leading-snug disabled:bg-slate-50"
                        />
                    </label>

                    {/* ─── Anhang ────────────────────────────────────────── */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                        <div className="inline-flex items-center gap-1.5 text-[11px] font-black text-slate-500 uppercase tracking-wider">
                            <Paperclip className="w-3.5 h-3.5" /> Anhang: Anforderungsblatt (PDF)
                        </div>
                        <ul className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                            {zeilen.map(z => (
                                <li key={z.id} className="text-xs text-slate-700">
                                    · {z.name}
                                    {z.eintrag && z.eintrag.angefordert_am && (
                                        <span className="text-slate-400"> (erneut — zuerst {datumDeutsch(z.eintrag.angefordert_am)})</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {(meldung || fehler) && (
                        <div className="bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 text-sm text-rose-800 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <span className="flex-1 min-w-0 whitespace-pre-line">
                                {meldung || fehler}
                                {!meldung && fehler && ' Es wurde nichts als angefordert festgehalten.'}
                            </span>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
                    <button type="button" onClick={onAbbrechen} disabled={gesperrt} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-800 disabled:opacity-40">
                        Abbrechen
                    </button>
                    <button
                        type="button"
                        onClick={absenden}
                        disabled={gesperrt}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
                    >
                        {laeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        {laeuft ? 'Wird versendet …' : 'Versenden'}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};
